import React from 'react';
import { useEntity, useRelatedEntities } from '@backstage/plugin-catalog-react';
import { parseEntityRef } from '@backstage/catalog-model';
import { C4DiagramCard } from '../C4DiagramCard/C4DiagramCard';
import { useGroups, useSystems } from '../../../hooks';
import {
  C4DiagramModel,
  C4DiagramEntity,
  C4DiagramRelation,
  toC4DiagramEntity,
} from '../C4DiagramCard/types';
// @ts-ignore
import * as uuid from 'uuid';

export const SystemContextDiagramCard = () => {
  const { entity } = useEntity();
  const relatedSystems = useRelatedEntities(entity, { kind: 'System' });
  const relatedProviders = useRelatedEntities(entity, { kind: 'Provider' });
  const relatedGroups = useRelatedEntities(entity, { kind: 'Group' });
  const systems = useSystems();
  const groups = useGroups();

  if (
    !relatedSystems.loading &&
    !relatedGroups.loading &&
    !relatedProviders.loading &&
    !systems.loading &&
    !groups.loading &&
    entity?.metadata
  ) {
    const model: C4DiagramModel = {
      type: 'context',
      context: toC4DiagramEntity(entity, 'container', true),
      entities: [],
      relations: [],
    };

    // Level 1 System Model
    const parentOfSystems = entity.relations
      ?.filter(it => it.type === 'parentOf')
      .map(it => parseEntityRef(it.targetRef))
      .filter(it => it.kind === 'system')
      .map(it => it.name);
    if (parentOfSystems) {
      // Entities
      for (const system of parentOfSystems) {
        const childSystem = relatedSystems.entities?.find(
          entity => entity.metadata.name === system,
        );
        if (childSystem) {
          for (const relation of childSystem.relations
            ? childSystem.relations
            : []) {
            if (
              relation.type === 'dependsOn' &&
              parseEntityRef(relation.targetRef).kind === 'system'
            ) {
              const relatedEntity = systems.entities!.find(
                it =>
                  it.metadata.name === parseEntityRef(relation.targetRef).name,
              )!;
              if (
                !parentOfSystems.find(it => it === relatedEntity.metadata.name)
              ) {
                pushEntity(model, toC4DiagramEntity(relatedEntity, 'context'));
                pushRelation(model, {
                  id: uuid.v4(),
                  source: entity.metadata.name,
                  target: relatedEntity.metadata.name,
                  text: 'depends on',
                });
              }
            } else if (
              relation.type === 'usedBy' &&
              parseEntityRef(relation.targetRef).kind === 'group'
            ) {
              const relatedEntity = groups.entities!.find(
                it =>
                  it.metadata.name === parseEntityRef(relation.targetRef).name,
              )!;
              pushEntity(model, toC4DiagramEntity(relatedEntity));
              pushRelation(model, {
                id: uuid.v4(),
                source: relatedEntity.metadata.name,
                target: entity.metadata.name,
                text: 'used by',
              });
            }
          }
        }
      }
    }

    // System Model
    const childOfSystems = entity.relations
      ?.filter(it => it.type === 'childOf')
      .map(it => parseEntityRef(it.targetRef))
      .filter(it => it.kind === 'system')
      .map(it => it.name);
    if (childOfSystems) {
      for (const system of childOfSystems) {
        const relatedEntity = relatedSystems.entities?.find(
          entity => entity.metadata.name === system,
        );
        if (relatedEntity) {
          pushEntity(model, toC4DiagramEntity(relatedEntity, 'context'));
          pushRelation(model, {
            id: uuid.v4(),
            source: relatedEntity!.metadata.name,
            target: entity.metadata.name,
            text: 'has part',
          });
        }
      }
    }
    const usedByGroups = entity.relations
      ?.filter(it => it.type === 'usedBy')
      .map(it => parseEntityRef(it.targetRef))
      .filter(it => it.kind === 'group')
      .map(it => it.name);
    if (usedByGroups) {
      for (const group of usedByGroups) {
        const relatedEntity = relatedGroups.entities?.find(
          entity => entity.metadata.name === group,
        );
        const relation = (entity.spec?.usedBy as any[])?.find(
          rel => rel.name === group,
        );
        if (relatedEntity) {
          pushEntity(model, toC4DiagramEntity(relatedEntity));
          pushRelation(model, {
            id: uuid.v4(),
            source: relatedEntity.metadata.name,
            target: entity.metadata.name,
            text: relation?.metadata?.description,
          });
        }
      }
    }
    const dependsOnSystems = entity.relations
      ?.filter(it => it.type === 'dependsOn')
      .map(it => parseEntityRef(it.targetRef))
      .filter(it => it.kind === 'system')
      .map(it => it.name);
    if (dependsOnSystems) {
      for (const system of dependsOnSystems) {
        const relatedEntity = relatedSystems.entities?.find(
          entity => entity.metadata.name === system,
        );
        const relationDependsOn = (entity.spec?.dependsOn as any[])?.find(
          rel => rel.name === system,
        );
        const relationDependencyOf = (
          relatedEntity?.spec?.dependencyOf as any[]
        )?.find(rel => rel.name === entity.metadata.name);
        const description = relationDependsOn?.metadata?.description
          ? relationDependsOn?.metadata?.description
          : relationDependencyOf?.metadata?.description;
        if (relatedEntity) {
          pushEntity(model, toC4DiagramEntity(relatedEntity, 'context'));
          pushRelation(model, {
            id: uuid.v4(),
            source: entity.metadata.name,
            target: relatedEntity!.metadata.name,
            text: description,
          });
        }
      }
    }
    const dependencyOfSystems = entity.relations
      ?.filter(it => it.type === 'dependencyOf')
      .map(it => parseEntityRef(it.targetRef))
      .filter(it => it.kind === 'system')
      .map(it => it.name);
    if (dependencyOfSystems) {
      for (const system of dependencyOfSystems) {
        const relatedEntity = relatedSystems.entities?.find(
          entity => entity.metadata.name === system,
        );
        const relationDependencyOf = (entity.spec?.dependencyOf as any[])?.find(
          rel => rel.name === system,
        );
        const relationDependsOn = (
          relatedEntity?.spec?.dependsOn as any[]
        )?.find(rel => rel.name === entity.metadata.name);
        const description = relationDependencyOf?.metadata?.description
          ? relationDependencyOf?.metadata?.description
          : relationDependsOn?.metadata?.description;
        if (relatedEntity) {
          pushEntity(model, toC4DiagramEntity(relatedEntity, 'context'));
          pushRelation(model, {
            id: uuid.v4(),
            source: relatedEntity!.metadata.name,
            target: entity.metadata.name,
            text: description,
          });
        }
      }
    }

    const consumesDataFrom = entity.relations
      ?.filter(it => it.type === 'consumesDataFrom')
      .map(it => parseEntityRef(it.targetRef))
      .filter(it => it.kind === 'provider')
      .map(it => it.name);
    if (consumesDataFrom) {
      for (const provider of consumesDataFrom) {
        const relatedEntity = relatedProviders.entities?.find(
          entity => entity.metadata.name === provider,
        );
        const relationConsumesDataFrom = (
          entity.spec?.consumesDataFrom as any[]
        )?.find(rel => rel.name === provider);
        const relationProvidesDataTo = (
          relatedEntity?.spec?.providesDataTo as any[]
        )?.find(rel => rel.name === entity.metadata.name);
        const description = relationConsumesDataFrom?.metadata?.description
          ? relationConsumesDataFrom?.metadata?.description
          : relationProvidesDataTo?.metadata?.description;
        if (relatedEntity) {
          pushEntity(model, toC4DiagramEntity(relatedEntity, 'context'));
          pushRelation(model, {
            id: uuid.v4(),
            source: entity.metadata.name,
            target: relatedEntity!.metadata.name,
            text: description,
          });
        }
      }
    }
    const providesDataTo = entity.relations
      ?.filter(it => it.type === 'providesDataTo')
      .map(it => parseEntityRef(it.targetRef))
      .filter(it => it.kind === 'system')
      .map(it => it.name);
    if (providesDataTo) {
      for (const system of providesDataTo) {
        const relatedEntity = relatedSystems.entities?.find(
          entity => entity.metadata.name === system,
        );
        const relationProvidesDataTo = (
          entity.spec?.providesDataTo as any[]
        )?.find(rel => rel.name === system);
        const relationConsumesDataFrom = (
          relatedEntity?.spec?.consumesDataFrom as any[]
        )?.find(rel => rel.name === entity.metadata.name);
        const description = relationProvidesDataTo?.metadata?.description
          ? relationProvidesDataTo?.metadata?.description
          : relationConsumesDataFrom?.metadata?.description;
        if (relatedEntity) {
          pushEntity(model, toC4DiagramEntity(relatedEntity, 'context'));
          pushRelation(model, {
            id: uuid.v4(),
            source: relatedEntity!.metadata.name,
            target: entity.metadata.name,
            text: description,
          });
        }
      }
    }
    return <C4DiagramCard model={model} />;
  }
  return <></>;
};

function pushEntity(model: C4DiagramModel, entity: C4DiagramEntity) {
  if (!model.entities.find(it => it.id === entity.id)) {
    model.entities.push(entity);
  }
}

function pushRelation(model: C4DiagramModel, relation: C4DiagramRelation) {
  if (
    !model.relations.find(
      it => it.source === relation.source && it.target == relation.target,
    )
  ) {
    model.relations.push(relation);
  }
}
